import * as React from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Button,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormHelperText,
} from '@mui/material';
import pxToRem from 'assets/theme/function/px-to-rem';
import { memo, useState, useEffect, useRef } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useAddProductItemMutation } from 'store/slices/product/product-item';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { ItemCreatRequest } from 'types/product/product-item';
import ItemGoldGem from 'component/product-item/item-gold-gem';
import ItemCard from 'component/product-item/item-card';
import ItemNoAds from 'component/product-item/item-no-ads';
import ItemSubscribe from 'component/product-item/item-subscribe';
import DialogIconClose from 'component/dialog-icon-close';
import colors from 'assets/theme/base/colors';
import rgba from 'assets/theme/function/rgba';
import { ProductItemTypeEnum } from 'types/product/product-item-type';
import SuccessSnackbarEffect from 'component/success-snackbar';

interface Props {
    productId?: string;
    setOpenDialogAddProductItem: React.Dispatch<React.SetStateAction<boolean>>;
    getItemList: () => void;
    dataProductId?: string;
}

const CreateProductItem: React.FC<Props> = memo((props: Props) => {
    const {
        productId,
        getItemList,
        setOpenDialogAddProductItem,
        dataProductId,
    } = props;
    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<ItemCreatRequest>();

    const [selectedCategory, setSelectedCategory] = useState('');
    const selectedValue = useWatch({ control, name: 'itemType' });
    const formRef = useRef<HTMLFormElement>(null);

    const [creatProductItemApi, { isSuccess, error, isError }] =
        useAddProductItemMutation();

    const getCardCategoryList = [
        {
            label: 'Gold',
            component: <ItemGoldGem errors={errors} control={control} />,
            key: ProductItemTypeEnum.GOLD,
        },
        {
            label: 'Gem',
            component: <ItemGoldGem errors={errors} control={control} />,
            key: ProductItemTypeEnum.GEM,
        },
        {
            label: 'Card',
            component: <ItemCard control={control} errors={errors} />,
            key: ProductItemTypeEnum.CARD,
        },
        {
            label: 'No Ads',
            component: <ItemNoAds errors={errors} control={control} />,
            key: ProductItemTypeEnum.NO_ADS,
        },
        {
            label: 'Subscribe',
            component: <ItemSubscribe errors={errors} control={control} />,
            key: ProductItemTypeEnum.SUBSCRIBE,
        },
        {
            label: 'avatar',
            component: <ItemGoldGem errors={errors} control={control} />,
            key: ProductItemTypeEnum.AVATAR,
        },
        {
            label: 'toon-tint-item',
            component: <ItemGoldGem errors={errors} control={control} />,
            key: ProductItemTypeEnum.TOONTINTITEM,
        },
    ];

    const dispatch = useAppDispatch();
    const onSubmit = (values: ItemCreatRequest) => {
        creatProductItemApi({
            ...values,
            productId: productId ?? dataProductId,
        });
    };

    const closeDialog = () => {
        setOpenDialogAddProductItem(false);
        reset();
    };

    useEffect(() => {
        setSelectedCategory(selectedValue || '');
    }, [selectedValue]);

    useEffect(() => {
        if (isSuccess) {
            closeDialog();
            getItemList();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
        if (isSuccess) {
            dispatch(
                openSnackbar({
                    message: productId
                        ? 'Product Price Update'
                        : 'Product Price Created',
                    severity: 'info',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
            closeDialog();
        }
    }, [isError, isSuccess]);

    return (
        <>
            <Box
                display={'flex'}
                justifyContent={'center'}
                bgcolor={rgba(colors.primary.main, 0.5)}
            >
                <DialogTitle textTransform={'uppercase'}>
                    ADD NEW ITEM
                </DialogTitle>
                <DialogIconClose handleClose={closeDialog} />
            </Box>

            <DialogContent>
                <Box
                    component="form"
                    ref={formRef}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Box display={'flex'} pt={pxToRem(15)}>
                        <FormControl error={Boolean(errors.itemType)} fullWidth>
                            <InputLabel id="product-type-label">
                                Product Item Type
                            </InputLabel>
                            <Controller
                                name="itemType"
                                control={control}
                                rules={{
                                    required: 'Product Item Type is required',
                                }}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <Select
                                        labelId="product-type-label"
                                        name={name}
                                        value={value || ''}
                                        label="Product Type"
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            setSelectedCategory(newValue);
                                            onChange(newValue);
                                        }}
                                    >
                                        {getCardCategoryList.map((category) => (
                                            <MenuItem
                                                key={category.key}
                                                value={category.key}
                                            >
                                                {category.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors.itemType && (
                                <FormHelperText>
                                    {errors.itemType.message?.toString()}
                                </FormHelperText>
                            )}
                        </FormControl>
                    </Box>

                    {selectedCategory && (
                        <Box width={'100%'} pt={pxToRem(15)}>
                            {
                                getCardCategoryList.find(
                                    (category) =>
                                        category.key === selectedCategory,
                                )?.component
                            }
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    sx={{ width: '50%' }}
                    onClick={() => formRef.current?.requestSubmit()}
                    variant={'contained'}
                >
                    Create Product Item
                </Button>
                <Button
                    sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                    onClick={() => closeDialog()}
                    variant={'contained'}
                >
                    Cancel
                </Button>
            </DialogActions>

            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Created an Product Item!'}
            />
        </>
    );
});

export default CreateProductItem;
